/**
 * The navigation sidebar's open/closed behaviour.
 *
 * ONE SIDEBAR, ONE STATE. The rows, the icons, the groups and the styling are
 * unchanged and still live in components/layout/Sidebar.tsx — this adds only the
 * question of what shape that sidebar is in, and it answers it in one place because
 * six things ask: the Studio's hamburger, the drawer's overlay, the Escape key, a
 * route change, the viewport crossing 900px, and the minimise control. Any of those
 * handled locally would be a second opinion about the same sidebar.
 *
 * THREE SHAPES, because a sidebar on a 1440px monitor and a sidebar on a 390px phone are
 * not the same object, and because the wide one is now the user's choice to narrow:
 *
 *   full column (> 900px)       the default: 200px, icons with labels beside them.
 *   compact column (> 900px)    the same column narrowed to its icons, because the user
 *                               asked for it. Remembered — see `compact`.
 *   drawer (≤ 900px)            the column leaves the layout entirely and `drawerOpen`
 *                               brings it back as an overlay, at full width with its
 *                               labels. Not remembered: a full-screen menu on load is
 *                               not a preference.
 *
 * WHAT `compact` IS NOT. There used to be a `collapsed` flag here, backed by a stored
 * `Lyrixa.navCollapsed` preference and a button beside the brand mark; both were removed at
 * the owner's request and neither has come back. This is a different preference, in a
 * different store (preferencesStore, beside the theme, because it is a remembered choice
 * about appearance), under a different key, set by a different control in a different place.
 * The old flag lived in `uiStore` and was toggled from two buttons — the sidebar's and a
 * duplicate in the Studio header — which is the arrangement that made it worth removing.
 * See the sidebar's own header for how the new control differs on screen.
 */
import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { NAV_DRAWER_QUERY, useMediaQuery } from './useMediaQuery';
import { usePreferencesStore } from '../stores/preferencesStore';
import { useUiStore } from '../stores/uiStore';

export interface NavSidebar {
  /** True on the widths where the sidebar is an overlay rather than a column. */
  drawer: boolean;
  /** True when the overlay drawer is showing. Always false outside drawer mode. */
  drawerOpen: boolean;
  /**
   * True when the column is minimised to its icons.
   *
   * ALWAYS FALSE IN DRAWER MODE, which is the coordination this hook exists to do. The
   * preference is about a column's width, and in drawer mode there is no column — the
   * sidebar is an overlay that either covers the page or is off it. A compact overlay would
   * be a 76px strip of unlabelled glyphs floating over a dimmed page: narrower than the
   * thing it is a menu for, and harder to read than the full one it replaced. So the stored
   * choice is ignored here and honoured again when the window widens, exactly as
   * `drawerOpen` is forced false the other way.
   */
  compact: boolean;
  /** Shows or hides the drawer. A no-op outside drawer mode. */
  toggle: () => void;
  /** Hides it. Used by the overlay, Escape, and navigation. */
  close: () => void;
  /**
   * Minimises or restores the column, and remembers which. A no-op in drawer mode, for
   * the reason `compact` gives.
   */
  toggleCompact: () => void;
}

export function useNavSidebar(): NavSidebar {
  const drawer = useMediaQuery(NAV_DRAWER_QUERY);
  const drawerOpen = useUiStore((s) => s.navDrawerOpen);
  const toggleNavDrawer = useUiStore((s) => s.toggleNavDrawer);
  const setNavDrawerOpen = useUiStore((s) => s.setNavDrawerOpen);
  // One field per selector: Zustand v5 compares with Object.is, so an object literal here
  // would be a new reference on every unrelated preferences change.
  const navCompact = usePreferencesStore((s) => s.navCompact);
  const toggleNavCompact = usePreferencesStore((s) => s.toggleNavCompact);
  const { pathname } = useLocation();

  const open = drawer && drawerOpen;
  const compact = !drawer && navCompact;

  const close = useCallback(() => setNavDrawerOpen(false), [setNavDrawerOpen]);

  /*
    Guarded on `drawer` rather than toggling unconditionally: outside drawer widths the
    sidebar is a permanent column and there is nothing to show or hide, so flipping the
    drawer flag there would leave a stale `true` waiting to open an overlay the moment
    the window narrowed. The only caller is the Studio's hamburger, which is itself only
    rendered in drawer mode — this is the belt to that braces.
  */
  const toggle = useCallback(() => {
    if (drawer) toggleNavDrawer();
  }, [drawer, toggleNavDrawer]);

  /*
    THE MINIMISE, guarded the mirror image of `toggle` above.

    In drawer mode the preference has no meaning (see `compact`), and the control that calls
    this is not rendered there — the drawer's brand slot holds its close button instead. The
    guard is what stops a stored `true` from being flipped to `false` by a press that could
    not have happened: without it, a caller in drawer mode would silently rewrite the
    preference the user set on their desktop.

    NOT CLEARED when the window narrows, unlike `drawerOpen`. That is deliberate and it is
    the difference between a preference and a piece of session state: rotating a tablet
    should not forget that someone likes their navigation compact, so the stored value is
    left alone and simply not applied while it cannot be. `compact` above is where that
    "not applied" happens, at render, so there is no effect to get out of step.
  */
  const toggleCompact = useCallback(() => {
    if (!drawer) toggleNavCompact();
  }, [drawer, toggleNavCompact]);

  /*
    Navigating closes the drawer. Not a convenience: on a phone the drawer covers the
    page, so tapping a nav row would otherwise land you on the new route with the menu
    still on top of it, and the only way out would be a control you have to find behind
    the thing you just used.

    Keyed on pathname, so it fires on the navigation rather than on the tap — which
    also covers the browser back button and a deep link followed from elsewhere.
  */
  useEffect(() => {
    setNavDrawerOpen(false);
  }, [pathname, setNavDrawerOpen]);

  /*
    Leaving drawer widths closes it too. A drawer left open while the viewport grows
    past 900px would be an overlay sitting on top of a layout that now has a permanent
    sidebar of its own — the navigation twice, once covering the other.
  */
  useEffect(() => {
    if (!drawer) setNavDrawerOpen(false);
  }, [drawer, setNavDrawerOpen]);

  /*
    Escape closes the drawer, and only when it is open — the listener is not installed
    otherwise, so the Studio's own Escape shortcut (close the tool panel, or leave the
    Studio) is untouched at every other moment.

    Capture phase with stopPropagation for the moment it IS open: the drawer is the
    topmost thing on screen and holds the focus, so Escape belongs to it and must not
    also reach the route underneath and navigate away from the page the user is
    looking at.
  */
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      close();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [open, close]);

  return { drawer, drawerOpen: open, compact, toggle, close, toggleCompact };
}
